import { useImperativeHandle, useRef, type Ref } from 'react';
import { observer } from 'mobx-react-lite';
import { useGlobalAudioPlayer } from 'react-use-audio-player';
import { useMessagingStore } from '../hooks/useMessagingStore.tsx';
import { useAudioRecorderContext } from '../contexts/recorder.tsx';
import { VoiceReader } from '../components/input/VoiceReader.tsx';
import { AudioSender } from '../components/input/AudioSender.tsx';
import {
  RealtimeVoiceSession,
  type RealtimeVoiceControls,
} from '../components/input/RealtimeVoiceSession.tsx';
import type { BrowserVoiceScreenSelection } from '../../../../../shared/ws-protocol.ts';
import type { WebsiteContext } from '../../../../../shared/website-context.ts';

export interface StageVoiceControls {
  stop: () => Promise<void>;
}

/** Keeps the advertised voice engine mounted and owns its media shutdown. */
export const StageVoice = observer(function StageVoice({
  screenSelection,
  websiteContext,
  onRecord,
  controlsRef,
}: {
  screenSelection: BrowserVoiceScreenSelection;
  websiteContext?: WebsiteContext;
  onRecord: (audio: string) => void;
  controlsRef?: Ref<StageVoiceControls>;
}) {
  const store = useMessagingStore();
  const { disableMicrophone } = useAudioRecorderContext();
  const { pause } = useGlobalAudioPlayer();
  const realtime = useRef<RealtimeVoiceControls>(null);
  useImperativeHandle(
    controlsRef,
    () => ({
      stop: async () => {
        const stopped = store.voiceEngine === 'realtime' ? realtime.current?.stop() : undefined;
        if (store.speechEnabled) {
          store.toggleSpeech();
        }
        store.setVoiceState('idle');
        if (store.voiceEngine === 'realtime') {
          await stopped;
        } else {
          pause();
          await disableMicrophone();
        }
      },
    }),
    [store, disableMicrophone, pause],
  );
  return (
    <>
      <VoiceReader />
      <AudioSender onRecord={onRecord} />
      <RealtimeVoiceSession
        ref={realtime}
        screenSelection={screenSelection}
        websiteContext={websiteContext}
      />
    </>
  );
});
